import React, { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Upload, Download, Search, Loader2, CheckCircle, Clock, AlertTriangle,
  FileSpreadsheet, ChevronLeft, ChevronRight, Package, X, Plus
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/services/api';

const STATUS_CONFIG = {
  matched: { label: 'Matched', className: 'bg-green-100 text-green-800' },
  under_review: { label: 'Under Review', className: 'bg-amber-100 text-amber-800' },
  pending_review: { label: 'Pending internal review by 1Buy.ai', className: 'bg-gray-100 text-gray-600' },
};

const MyItems = () => {
  const fileRef = useRef(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [batchPolling, setBatchPolling] = useState(null);
  const [summary, setSummary] = useState(null);

  // Request New Component
  const [showRequest, setShowRequest] = useState(false);
  const [reqForm, setReqForm] = useState({ mpn: '', price: '', quantity: '', moq: '', incoTerms: 'EXW', packaging: 'Tape & Reel', distType: 'authorized', countryPref: 'any', countryExclude: 'none', notes: '' });
  const [reqErrors, setReqErrors] = useState({});
  const [reqSubmitting, setReqSubmitting] = useState(false);

  const fetchItems = useCallback(async (p = page, q = search) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, page_size: 50 });
      if (q.trim()) params.set('search', q.trim());
      const res = await api.get(`/v1/my-items?${params}`);
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
    } catch {
      toast.error('Failed to load items');
    }
    setLoading(false);
  }, [page, search]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchItems(1, search);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Only .xlsx files accepted');
      return;
    }
    setUploading(true);
    setSummary(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/v1/my-items/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.error) {
        toast.error(res.data.error);
        setUploading(false);
        return;
      }
      toast.success(`Upload started — ${res.data.rows_total} rows detected`);
      const batchId = res.data.batch_id;

      // Poll status
      const poll = setInterval(async () => {
        try {
          const statusRes = await api.get(`/v1/my-items/upload/${batchId}/status`);
          const d = statusRes.data;
          if (d.status === 'completed') {
            clearInterval(poll);
            setBatchPolling(null);
            setUploading(false);
            setSummary(d);
            setPage(1);
            fetchItems(1, search);
            toast.success('Upload complete');
          } else if (d.status === 'failed') {
            clearInterval(poll);
            setBatchPolling(null);
            setUploading(false);
            toast.error('Upload processing failed');
          }
        } catch { /* keep polling */ }
      }, 3000);
      setBatchPolling(poll);
    } catch {
      toast.error('Upload failed');
      setUploading(false);
    }
    if (fileRef.current) fileRef.current.value = '';
  };

  useEffect(() => {
    return () => { if (batchPolling) clearInterval(batchPolling); };
  }, [batchPolling]);

  const validateReqForm = () => {
    const errs = {};
    if (!reqForm.mpn || reqForm.mpn.length < 3) errs.mpn = 'MPN must be at least 3 characters';
    else if (!/^[A-Za-z0-9\-_/:\.()]+$/.test(reqForm.mpn)) errs.mpn = 'Invalid MPN format';
    if (reqForm.price && (isNaN(Number(reqForm.price)) || Number(reqForm.price) < 0)) errs.price = 'Price must be >= 0';
    if (reqForm.quantity && (isNaN(Number(reqForm.quantity)) || !Number.isInteger(Number(reqForm.quantity)) || Number(reqForm.quantity) < 0)) errs.quantity = 'Must be a positive integer';
    setReqErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmitRequest = async () => {
    if (!validateReqForm()) return;
    setReqSubmitting(true);
    try {
      const res = await api.post('/components/request', {
        mpn: reqForm.mpn, price: parseFloat(reqForm.price) || 0, quantity: parseInt(reqForm.quantity) || 0,
        moq: parseInt(reqForm.moq) || 0, inco_terms: reqForm.incoTerms, packaging: reqForm.packaging,
        dist_type: reqForm.distType, country_pref: reqForm.countryPref, country_exclude: reqForm.countryExclude, notes: reqForm.notes,
      });
      toast.success(res.data.message || 'Component request submitted');
      setShowRequest(false);
      setReqForm({ mpn: '', price: '', quantity: '', moq: '', incoTerms: 'EXW', packaging: 'Tape & Reel', distType: 'authorized', countryPref: 'any', countryExclude: 'none', notes: '' });
      fetchItems(1, '');
    } catch (e) { toast.error(e.response?.data?.detail || 'Request failed'); }
    finally { setReqSubmitting(false); }
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${d.getDate().toString().padStart(2,'0')} ${months[d.getMonth()]} ${d.getFullYear()} ${d.getHours().toString().padStart(2,'0')}:${d.getMinutes().toString().padStart(2,'0')}`;
  };

  return (
    <Layout title="My Parts" subtitle="Manage your components, request new MPN and map MPNs to your Custom Part Numbers">
      <div className="space-y-4" data-testid="my-items-page">
        {/* Action Bar */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
              data-testid="upload-items-btn"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {uploading ? 'Processing...' : 'Upload Items'}
            </Button>
            <input ref={fileRef} type="file" className="hidden" accept=".xlsx,.xls" onChange={handleUpload} data-testid="file-input" />
            <Button onClick={() => setShowRequest(true)} variant="outline" className="border-green-300 text-green-700 hover:bg-green-50 gap-2" data-testid="request-new-component-btn">
              <Plus className="h-4 w-4" /> Request New
            </Button>
            <a
              href="/static/templates/my_items_template.xlsx"
              download
              className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 hover:underline"
              data-testid="download-template-link"
            >
              <Download className="h-4 w-4" /> Download Template
            </a>
          </div>
          <form onSubmit={handleSearch} className="flex items-center gap-2">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search CPN, MPN, or Description..."
                className="pl-9 w-72"
                data-testid="search-input"
              />
              {search && (
                <button type="button" onClick={() => { setSearch(''); setPage(1); fetchItems(1, ''); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <Button type="submit" size="sm" variant="outline" data-testid="search-btn">Search</Button>
          </form>
        </div>

        {/* Upload Summary Banner */}
        {summary && (
          <Card className="border-green-200 bg-green-50 shadow-sm">
            <CardContent className="py-3 px-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-sm font-medium text-green-800">
                  Upload complete: <strong>{summary.rows_matched}</strong> matched, <strong>{summary.rows_unmatched}</strong> under review
                  {summary.rows_error > 0 && <>, <strong className="text-red-600">{summary.rows_error}</strong> errors</>}
                  {summary.rows_updated > 0 && <>, <strong>{summary.rows_updated}</strong> updated</>}
                </span>
              </div>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setSummary(null)} data-testid="dismiss-summary">
                <X className="h-3.5 w-3.5" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Items Table */}
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-16 text-gray-400">
                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading items...
              </div>
            ) : items.length === 0 ? (
              <div className="text-center py-16 text-gray-400" data-testid="empty-state">
                <Package className="h-12 w-12 mx-auto mb-3 opacity-40" />
                <p className="font-medium text-gray-600 mb-1">No items uploaded yet</p>
                <p className="text-sm">Download the template to get started.</p>
                <a
                  href="/static/templates/my_items_template.xlsx"
                  download
                  className="inline-flex items-center gap-1.5 mt-4 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-100"
                >
                  <FileSpreadsheet className="h-4 w-4" /> Download Template
                </a>
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="w-40">CPN</TableHead>
                      <TableHead className="w-48">MPN</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="w-60">Status</TableHead>
                      <TableHead className="w-36">Batch ID</TableHead>
                      <TableHead className="w-44">Uploaded Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((item, idx) => {
                      const st = STATUS_CONFIG[item.status] || STATUS_CONFIG.pending_review;
                      return (
                        <TableRow key={item.id || idx} data-testid={`item-row-${idx}`}>
                          <TableCell className="font-medium text-gray-900 text-sm">{item.cpn || <span className="text-gray-300">—</span>}</TableCell>
                          <TableCell className="font-mono text-sm text-blue-700">{item.mpn}</TableCell>
                          <TableCell className="text-sm text-gray-600 max-w-xs truncate">{item.description || <span className="text-gray-300">—</span>}</TableCell>
                          <TableCell>
                            <Badge className={`text-xs ${st.className}`} data-testid={`status-badge-${idx}`}>{st.label}</Badge>
                          </TableCell>
                          <TableCell className="text-xs text-gray-400 font-mono">{item.batch_id?.replace('batch_', '') || '-'}</TableCell>
                          <TableCell className="text-sm text-gray-500">{formatDate(item.uploaded_at)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>

                {/* Pagination */}
                <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
                  <span className="text-sm text-gray-500">
                    Showing {(page - 1) * 50 + 1}–{Math.min(page * 50, total)} of {total.toLocaleString()} items
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline" size="sm"
                      disabled={page <= 1}
                      onClick={() => { setPage(p => p - 1); }}
                      data-testid="prev-page"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
                    <Button
                      variant="outline" size="sm"
                      disabled={page >= totalPages}
                      onClick={() => { setPage(p => p + 1); }}
                      data-testid="next-page"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Request New Component Dialog */}
      <Dialog open={showRequest} onOpenChange={setShowRequest}>
        <DialogContent className="max-w-2xl" data-testid="request-component-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Request New Component</DialogTitle>
            <CardDescription className="text-xs">Submit a new component for validation by our data team.</CardDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">MPN *</Label>
              <Input value={reqForm.mpn} onChange={e => setReqForm({...reqForm, mpn: e.target.value})} placeholder="e.g., STM32F407VGT6" className="mt-1 h-8" data-testid="req-mpn" />
              {reqErrors.mpn && <p className="text-[10px] text-red-500 mt-0.5">{reqErrors.mpn}</p>}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Price ($)</Label><Input type="number" step="0.001" value={reqForm.price} onChange={e => setReqForm({...reqForm, price: e.target.value})} placeholder="8.45" className="mt-1 h-8" data-testid="req-price" /></div>
              <div><Label className="text-xs">Annual Qty</Label><Input type="number" value={reqForm.quantity} onChange={e => setReqForm({...reqForm, quantity: e.target.value})} placeholder="10000" className="mt-1 h-8" data-testid="req-quantity" /></div>
              <div><Label className="text-xs">MOQ</Label><Input type="number" value={reqForm.moq} onChange={e => setReqForm({...reqForm, moq: e.target.value})} placeholder="100" className="mt-1 h-8" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Inco Terms</Label><Select value={reqForm.incoTerms} onValueChange={v => setReqForm({...reqForm, incoTerms: v})}><SelectTrigger className="mt-1 h-8"><SelectValue /></SelectTrigger><SelectContent>{['EXW','FOB','CIF','DDP','DAP'].map(t=><SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs">Packaging</Label><Select value={reqForm.packaging} onValueChange={v => setReqForm({...reqForm, packaging: v})}><SelectTrigger className="mt-1 h-8"><SelectValue /></SelectTrigger><SelectContent>{[['Tape & Reel','Tape & Reel'],['Tray','Tray'],['Tube','Tube'],['Bulk','Bulk']].map(([v,l])=><SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs">Distributor</Label><Select value={reqForm.distType} onValueChange={v => setReqForm({...reqForm, distType: v})}><SelectTrigger className="mt-1 h-8"><SelectValue /></SelectTrigger><SelectContent>{[['authorized','Authorized'],['independent','Independent'],['any','Any']].map(([v,l])=><SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div><Label className="text-xs">Notes</Label><Input value={reqForm.notes} onChange={e => setReqForm({...reqForm, notes: e.target.value})} placeholder="Context for data team..." className="mt-1 h-8" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowRequest(false)}>Cancel</Button>
            <Button size="sm" onClick={handleSubmitRequest} disabled={reqSubmitting} className="bg-green-600 hover:bg-green-700 text-white" data-testid="submit-component-request">{reqSubmitting ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null} Submit Request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default MyItems;
